/**
 * /progress-billings — Progress Billings list page (AIA G702/G703).
 *
 * Project selector at top → table of PBs for that project.
 * Actions: Submit, Approve, Invoice, PDF download.
 * "New Progress Billing" dialog.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useState, useCallback, useEffect } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { $fetch } from "ofetch";

import { api, downloadFile, errorMessage, getToken } from "@/lib/api";
import { FileDropZone } from "@/components/FileDropZone";
import {
  projectsFullListQueryOptions,
  progressBillingsQueryOptions,
  projectKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import { SovImportDialog } from "@/components/SovImportDialog";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";
import { gridDefaults } from "@/grids/gridDefaults";
import type {
  CascadeReport,
  LegacyProgressBillingLinePage,
  ProgressBilling,
  ProgressBillingInitialization,
  ProgressBillingReconciliation,
  ProgressBillingReduced,
  ProgressBillingSummary,
  ProgressBillingStatus,
  ProgressBillingValidation,
  ProgressBillingValidationIssue,
} from "@/lib/types";

interface ProgressBillingsSearch {
  project?: string;
}

interface OwnerSovItem {
  id: string;
  item_code: string;
  description: string;
  billing_type: string;
}

interface OwnerContractWorkspace {
  contract_id: string;
  contract_no: string;
  title: string;
  sov_items: OwnerSovItem[];
}

const LAST_PROJECT_KEY = "ledger.progressBillings.lastProject";

type OverbillBlocker = {
  code?: string;
  message?: string;
  details?: { allowed?: string; attempted?: string; excess?: string; progress_billing_line_id?: string };
};

/** Prefer domain error.message + overbill details over ofetch's "[POST] …: 409". */
function progressBillingBlockers(err: unknown): OverbillBlocker[] {
  const blockers = (
    err as {
      data?: {
        error?: {
          details?: { blockers?: OverbillBlocker[] };
        };
      };
    }
  )?.data?.error?.details?.blockers;
  return Array.isArray(blockers) ? blockers : [];
}

/** Turn a cascade report into what actually happened, never a blanket claim. */
function describeCascade(
  report: CascadeReport | null | undefined,
): { message: string; warn: boolean } {
  const apps = report?.applications ?? [];
  if (!apps.length) return { message: "Progress billing updated", warn: false };

  const list = (nos: number[]) => nos.map((n) => `#${String(n).padStart(2, "0")}`).join(", ");
  const resubmit = report?.needs_resubmission ?? [];
  const capped = apps.filter((a) => a.lines_capped.length);
  const carried = apps.filter((a) => a.lines_carried.length);

  const notes: string[] = [];
  if (resubmit.length) {
    notes.push(`${list(resubmit)} went back to draft and must be resubmitted`);
  }
  if (capped.length) {
    notes.push(
      `${list(capped.map((a) => a.application_no))} had lines that could not hold their % complete`,
    );
  }
  if (carried.length) {
    notes.push(
      `${list(carried.map((a) => a.application_no))} kept lines with no scheduled value as billed`,
    );
  }

  const updated = `Updated — ${list(apps.map((a) => a.application_no))} recalculated`;
  if (!notes.length) return { message: `${updated} from % complete`, warn: false };
  return { message: `${updated}. ${notes.join("; ")}.`, warn: true };
}

function moneyLabel(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function progressBillingInvoiceError(err: unknown): string {
  const base = errorMessage(err);
  // Never surface ofetch's bare "[POST] …: 409" when we have a domain message.
  if (base && !/^\s*\[(GET|POST|PUT|PATCH|DELETE)\]/i.test(base)) {
    const first = progressBillingBlockers(err)[0];
    const d = first?.details;
    if (d?.allowed != null && d?.attempted != null && !base.includes("Authorized (SOV)")) {
      const excess = d.excess != null ? ` Over by ${moneyLabel(d.excess)}.` : "";
      return (
        `${base} Authorized (SOV) ${moneyLabel(d.allowed)}; completed + stored ` +
        `${moneyLabel(d.attempted)}.${excess}`
      );
    }
    return base;
  }
  const first = progressBillingBlockers(err)[0];
  const d = first?.details;
  if (d?.allowed != null && d?.attempted != null) {
    const excess = d.excess != null ? ` Over by ${moneyLabel(d.excess)}.` : "";
    return (
      `${first?.message ?? "Line exceeds its authorized billable amount."} ` +
      `Authorized (SOV) ${moneyLabel(d.allowed)}; completed + stored ${moneyLabel(d.attempted)}.${excess}`
    );
  }
  return first?.message || base || "Could not post this application to the ledger.";
}

function isOverbillError(err: unknown): boolean {
  const code = (err as { data?: { error?: { code?: string } } })?.data?.error?.code;
  if (code === "owner_sov_max_exceeded") return true;
  return progressBillingBlockers(err).some((b) => b.code === "OWNER_SOV_MAX_EXCEEDED");
}

export const Route = createFileRoute("/_auth/progress-billings/")({
  validateSearch: (s: Record<string, unknown>): ProgressBillingsSearch => ({
    project: typeof s.project === "string" ? s.project : undefined,
  }),
  component: ProgressBillingsPage,
});

// ── Status colours ────────────────────────────────────────────

const PB_STATUS_COLORS: Record<ProgressBillingStatus, { bg: string; fg: string }> = {
  draft:     { bg: "#e2e8f0", fg: "#475569" },
  submitted: { bg: "#dbeafe", fg: "#1e40af" },
  approved:  { bg: "#d1fae5", fg: "#065f46" },
  invoiced:  { bg: "#dcfce7", fg: "#166534" },
  paid:      { bg: "#f0fdf4", fg: "#15803d" },
  rejected:  { bg: "#fee2e2", fg: "#991b1b" },
  reversed:  { bg: "#f1f5f9", fg: "#64748b" },
};

function fmt(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

// ── Page component ────────────────────────────────────────────

function ProgressBillingsPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;

  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate({ from: Route.fullPath });
  const { project: urlProjectId } = Route.useSearch();

  const projectsQuery = useQuery(projectsFullListQueryOptions({ limit: 500 }));
  const projects = projectsQuery.data?.items ?? [];
  const [projectId, setProjectIdState] = useState<string>("");
  const [billingPage, setBillingPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProgressBilling | null>(null);
  const [uploadingSov, setUploadingSov] = useState(false);
  const [importing, setImporting] = useState(false);
  const [adoptingLegacy, setAdoptingLegacy] = useState(false);
  const [invoiceBlock, setInvoiceBlock] = useState<{
    pbId: string;
    message: string;
    allowed?: string;
    attempted?: string;
    excess?: string;
  } | null>(null);
  const [adjusting, setAdjusting] = useState<ProgressBillingSummary | null>(null);
  const [adjustReason, setAdjustReason] = useState("");
  const [overbillFix, setOverbillFix] = useState<ProgressBillingSummary | null>(null);
  const [reduceReason, setReduceReason] = useState("");

  const setProjectId = useCallback(
    (id: string) => {
      setProjectIdState(id);
      setBillingPage(0);
      if (id) localStorage.setItem(LAST_PROJECT_KEY, id);
      navigate({ search: (prev) => ({ ...prev, project: id || undefined }), replace: true });
    },
    [navigate],
  );

  // Pick a project without the user having to: URL param first (deep link /
  // "Recent pages"), then the only project if there's just one, then the
  // last one this browser used. Only runs once projects have loaded and
  // nothing is selected yet.
  useEffect(() => {
    if (projectId || projects.length === 0) return;
    const lastUsed = localStorage.getItem(LAST_PROJECT_KEY);
    const candidate =
      (urlProjectId && projects.some((p) => p.id === urlProjectId) && urlProjectId) ||
      (projects.length === 1 && projects[0].id) ||
      (lastUsed && projects.some((p) => p.id === lastUsed) && lastUsed) ||
      "";
    if (candidate) setProjectId(candidate);
  }, [projectId, projects, urlProjectId, setProjectId]);

  const billingPageSize = 100;
  const pbQuery = useQuery({
    ...progressBillingsQueryOptions(projectId || null, {
      limit: billingPageSize,
      offset: billingPage * billingPageSize,
    }),
    enabled: Boolean(projectId),
  });
  const pbs = pbQuery.data?.items ?? [];
  const totalBillings = pbQuery.data?.total ?? 0;

  // Pre-flight every application that still has to clear the SOV check —
  // anything not yet on the ledger. An overbill is fatal at submit, approve
  // and post alike, so finding out by clicking wastes the trip; the row says
  // it up front instead. Invoiced and paid rows are already past the gate.
  const postableIds = pbs
    .filter(
      (pb) =>
        !pb.ar_invoice_id
        && (pb.status === "draft"
          || pb.status === "submitted"
          || pb.status === "approved"),
    )
    .map((pb) => pb.id);
  const preflightQueries = useQueries({
    queries: postableIds.flatMap((pbId) => [
      {
        // Every reason this application would be refused, not just overbilling:
        // an unmapped SOV line, a closed period, a duplicate period and a zero
        // payment all fail the same way, and all used to fail after the click.
        queryKey: ["progress-billing-validation", pbId],
        queryFn: () => api<ProgressBillingValidation>(
          `/api/projects/progress-billings/${pbId}/validation?stage=submit`,
        ),
        staleTime: 30_000,
        retry: false,
        meta: { skipErrorToast: true },
      },
      {
        queryKey: ["progress-billing-reconciliation", pbId],
        queryFn: () => api<ProgressBillingReconciliation>(
          `/api/projects/progress-billings/${pbId}/reconciliation`,
        ),
        staleTime: 30_000,
        retry: false,
        meta: { skipErrorToast: true },
      },
    ]),
  });
  const blockersByPb = new Map<string, ProgressBillingValidationIssue[]>();
  const reconByPb = new Map<string, ProgressBillingReconciliation>();
  postableIds.forEach((pbId, i) => {
    const validation = preflightQueries[i * 2]?.data as
      | ProgressBillingValidation | undefined;
    const recon = preflightQueries[i * 2 + 1]?.data as
      | ProgressBillingReconciliation | undefined;
    if (validation?.blockers?.length) blockersByPb.set(pbId, validation.blockers);
    if (recon) reconByPb.set(pbId, recon);
  });
  const overbillByPb = new Map<string, ProgressBillingReconciliation>();
  reconByPb.forEach((recon, pbId) => {
    if (Number(recon.overbilling_amount) > 0) overbillByPb.set(pbId, recon);
  });
  const overbillAmount = (pbId: string) =>
    Number(overbillByPb.get(pbId)?.overbilling_amount ?? 0);
  /** Short label for the row badge — the overbill has a number worth showing. */
  const blockerLabel = (pbId: string): string | null => {
    const blockers = blockersByPb.get(pbId);
    if (!blockers?.length) return null;
    if (overbillByPb.has(pbId)) {
      return `${moneyLabel(overbillAmount(pbId))} over contract · Review`;
    }
    return blockers.length === 1
      ? "Won't submit yet · Review"
      : `${blockers.length} things to fix · Review`;
  };
  const legacyLinesQuery = useQuery({
    queryKey: ["legacy-progress-billing-lines", projectId, "count"],
    queryFn: () => api<LegacyProgressBillingLinePage>(
      `/api/projects/${projectId}/progress-billings/legacy-lines?limit=1&offset=0`,
    ),
    enabled: Boolean(projectId),
  });
  const legacyLineCount = legacyLinesQuery.data?.total ?? 0;
  const ownerContractQuery = useQuery({
    queryKey: ["owner-contract-workspace", projectId],
    queryFn: () => api<OwnerContractWorkspace>(
      `/api/projects/${projectId}/owner-contract`,
    ),
    // Asked for on every job, not just ones with legacy lines: the answer is
    // what decides whether this job's schedule comes from a contract, and the
    // guidance below is useless if it only appears after a legacy backfill.
    enabled: Boolean(projectId),
    retry: false,
    staleTime: 30_000,
    // Most jobs have no owner contract. That is a normal answer, not a failed
    // load, so it must not raise the global "Couldn't load data" toast.
    meta: { skipErrorToast: true },
  });

  // ── Mutations ─────────────────────────────────────────────────

  const submitMut = useMutation({
    mutationFn: (pbId: string) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}/submit`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success("Submitted");
    },
    onError: (err: Error) => toast.error(`Submit failed: ${err.message}`),
  });

  const approveMut = useMutation({
    mutationFn: (pbId: string) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}/approve`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success("Approved");
    },
    onError: (err: Error) => toast.error(`Approve failed: ${err.message}`),
  });

  const reopenMut = useMutation({
    mutationFn: (pbId: string) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}/reopen`, { method: "POST" }),
    onSuccess: (billing) => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success("Rejected application reopened as a draft");
      setEditing(billing);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (pbId: string) =>
      api(`/api/projects/progress-billings/${pbId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success("Draft deleted");
    },
    // Anything past draft is refused (409) — show why rather than failing silently.
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const reduceToAuthorizedMut = useMutation({
    mutationFn: ({ pbId, reason }: { pbId: string; reason: string }) =>
      api<ProgressBillingReduced>(
        `/api/projects/progress-billings/${pbId}/reduce-to-authorized`,
        {
          method: "POST",
          body: { reason, reversal_date: new Date().toISOString().slice(0, 10) },
        },
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      qc.invalidateQueries({ queryKey: ["progress-billing-reconciliation"] });
      setOverbillFix(null);
      const lines = result.lines.length;
      toast.success(
        `Reduced by ${moneyLabel(result.total_reduction)} across ${lines} line`
        + `${lines === 1 ? "" : "s"} — application #${result.progress_billing.application_no} `
        + "is a draft again. Review it, then submit.",
      );
    },
    onError: (err: unknown) => toast.error(progressBillingInvoiceError(err)),
  });

  const invoiceMut = useMutation({
    mutationFn: (pbId: string) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}/invoice`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success("AR invoice created");
    },
    onError: (err: unknown, pbId) => {
      const message = progressBillingInvoiceError(err);
      const d = progressBillingBlockers(err)[0]?.details;
      if (isOverbillError(err) || d?.allowed != null) {
        setInvoiceBlock({
          pbId,
          message,
          allowed: d?.allowed,
          attempted: d?.attempted,
          excess: d?.excess,
        });
        return;
      }
      toast.error(message);
    },
  });

  const loadDraftForEdit = useMutation({
    mutationFn: (pbId: string) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}`),
    onSuccess: (billing) => setEditing(billing),
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const openForAdjustmentMut = useMutation({
    mutationFn: ({ pbId, reason }: { pbId: string; reason: string }) =>
      api<ProgressBilling>(`/api/projects/progress-billings/${pbId}/open-for-adjustment`, {
        method: "POST",
        body: {
          reason,
          reversal_date: new Date().toISOString().slice(0, 10),
        },
      }),
    onSuccess: (billing) => {
      setAdjusting(null);
      setAdjustReason("");
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      toast.success(
        `Application #${billing.application_no} is a draft again` +
          (billing.revision_no ? ` (rev ${billing.revision_no})` : "") +
          ". Later apps keep their % complete when you save.",
      );
      setEditing(billing);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const handleDownload = useCallback(
    (pb: ProgressBillingSummary) => {
      downloadFile(
        `/api/projects/progress-billings/${pb.id}/pdf`,
        `G702-G703-App${pb.application_no}-${pb.billing_date}.pdf`,
      ).catch((err: Error) => toast.error(err.message));
    },
    [toast],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Progress Billings
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">AIA G702/G703</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="w-[360px]">
        <ProjectIdSelect
          label="Project"
          value={projectId}
          onChange={setProjectId}
          statusFilter={STANDARD_PROJECT_STATUSES}
          placeholder="Search project code or name…"
        />
        </div>

        <div className="flex-1" />

        {projectId && (
          <>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {totalBillings} application{totalBillings !== 1 ? "s" : ""}
            </span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setUploadingSov(true)}
              title="Builds a standalone draft pay application from an Excel SOV. On a job that bills against an owner contract, use + New Progress Billing instead — it loads the contract schedule already linked."
            >
              Upload SOV (Excel)
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setImporting(true)}
              title="Reads a contract or SOV exhibit PDF with AI into a standalone draft pay application. It does not link the lines to an owner contract SOV."
            >
              Read SOV from PDF
            </Button>
            {ownerContractQuery.data && legacyLineCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAdoptingLegacy(true)}
              >
                Adopt legacy billings ({legacyLineCount})
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() => setCreating(true)}
            >
              + New Progress Billing
            </Button>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {projectId && ownerContractQuery.data && (
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
            This job has an owner contract SOV ({ownerContractQuery.data.contract_no}).
            The schedule comes from that contract and applications bill against it, so use{" "}
            <strong>+ New Progress Billing</strong> for monthly apps — it loads the contract
            schedule with every line already linked. <strong>Upload SOV (Excel)</strong> and{" "}
            <strong>Read SOV from PDF</strong> build a separate, unlinked schedule that can
            disagree with the contract and will be refused when the application is posted.
          </Alert>
        )}
        {!projectId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
            Select a project above to view its progress billing applications.
          </Typography>
        ) : pbQuery.isLoading ? (
          <Box display="flex" justifyContent="center" pt={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>App #</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Billed date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                  Completed &amp; stored
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                  Current due
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                  Less retention
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pbs.map((pb) => {
                const col = PB_STATUS_COLORS[pb.status] ?? PB_STATUS_COLORS.draft;
                return (
                  <TableRow key={pb.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      #{pb.application_no}{pb.revision_no ? ` · Rev ${pb.revision_no}` : ""}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {pb.period_from} → {pb.period_to}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{pb.billing_date}</TableCell>
                    <TableCell>
                      <span
                        className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                        style={{ background: col.bg, color: col.fg }}
                      >
                        {pb.status}
                      </span>
                      {blockerLabel(pb.id) && (
                        <button
                          onClick={() => {
                            setOverbillFix(pb);
                            setReduceReason(
                              `Reduced to the authorized SOV amount on application #${pb.application_no}`,
                            );
                          }}
                          className="mt-1 block rounded px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 bg-amber-100 hover:bg-amber-200 dark:text-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60"
                          title="This application will be refused as it stands — see why"
                        >
                          {blockerLabel(pb.id)}
                        </button>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                      {fmt(Number(pb.total_completed_to_date) + Number(pb.total_stored_materials))}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                      {fmt(pb.current_due)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                      {fmt(pb.less_retention)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {pb.status === "draft" && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                              disabled={submitMut.isPending}
                              onClick={() => submitMut.mutate(pb.id)}
                            >
                              Submit
                            </Button>
                            {/* A draft was previously immutable — no edit and no
                                delete existed, so a wrong period or SOV could only
                                be escaped by submitting it. */}
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                              disabled={loadDraftForEdit.isPending}
                              onClick={() => loadDraftForEdit.mutate(pb.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                              disabled={deleteMut.isPending}
                              onClick={() => {
                                if (confirm(`Delete draft application #${pb.application_no}?`)) {
                                  deleteMut.mutate(pb.id);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        {pb.status === "submitted" && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                            disabled={approveMut.isPending}
                            onClick={() => approveMut.mutate(pb.id)}
                          >
                            Approve &amp; post
                          </Button>
                        )}
                        {pb.status === "rejected" && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                            disabled={reopenMut.isPending}
                            onClick={() => reopenMut.mutate(pb.id)}
                          >
                            Reopen &amp; edit
                          </Button>
                        )}
                        {/* Approval now books the billing, so this state only
                            exists on applications approved before that change.
                            Give them a way onto the ledger too. */}
                        {pb.status === "approved" && !pb.ar_invoice_id && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                            disabled={invoiceMut.isPending || overbillByPb.has(pb.id)}
                            onClick={() => invoiceMut.mutate(pb.id)}
                            title={
                              overbillByPb.has(pb.id)
                                ? `Billed ${moneyLabel(overbillByPb.get(pb.id))} more than the `
                                  + "owner SOV authorizes, so this cannot post. Use Adjust to "
                                  + "correct the amounts, or raise the SOV with a change order."
                                : "Create and post the AR invoice for this application."
                            }
                          >
                            Post to ledger
                          </Button>
                        )}
                        {(pb.status === "submitted"
                          || pb.status === "approved"
                          || pb.status === "invoiced") && (
                          <Button
                            size="small"
                            /* When posting is blocked this is the only way
                               forward, so it stops being a quiet text link. */
                            variant={overbillByPb.has(pb.id) ? "contained" : "text"}
                            color={overbillByPb.has(pb.id) ? "warning" : "primary"}
                            sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                            disabled={openForAdjustmentMut.isPending}
                            onClick={() => {
                              setAdjusting(pb);
                              setAdjustReason("");
                            }}
                            title={
                              overbillByPb.has(pb.id)
                                ? "Reopen this application as a draft so the billed amounts "
                                  + "can be brought within the authorized SOV."
                                : "Reopen as a draft. Later apps keep their % complete when you save."
                            }
                          >
                            Adjust
                          </Button>
                        )}
                        <button
                          title="Download G702/G703 PDF"
                          className="text-blue-600 hover:text-blue-800 text-[11px] font-medium ml-1"
                          onClick={() => handleDownload(pb)}
                        >
                          PDF
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {pbs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <span className="text-xs text-slate-400 italic">
                      No progress billing applications for this project.
                    </span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        {totalBillings > billingPageSize && (
          <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-200 dark:border-slate-700">
            <Button
              size="small"
              disabled={billingPage === 0}
              onClick={() => setBillingPage((page) => Math.max(0, page - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-slate-500">
              {billingPage * billingPageSize + 1}–{Math.min((billingPage + 1) * billingPageSize, totalBillings)} of {totalBillings}
            </span>
            <Button
              size="small"
              disabled={(billingPage + 1) * billingPageSize >= totalBillings}
              onClick={() => setBillingPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {creating && projectId && (
        <NewProgressBillingDialog
          projectId={projectId}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <NewProgressBillingDialog
          key={editing.id}
          projectId={projectId}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {uploadingSov && projectId && (
        <UploadSovDialog
          hasOwnerContract={Boolean(ownerContractQuery.data)}
          onSetUpContract={() => {
            setUploadingSov(false);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            navigate({ to: "/contract-work-packages" as any, search: { create: true, project: projectId } as any });
          }}
          projectCode={projects.find((p) => p.id === projectId)?.code ?? ""}
          onClose={() => setUploadingSov(false)}
          onDone={() => {
            setUploadingSov(false);
            qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
          }}
        />
      )}

      {importing && projectId && (
        <SovImportDialog
          projectId={projectId}
          onClose={() => setImporting(false)}
        />
      )}

      {adoptingLegacy && projectId && ownerContractQuery.data && (
        <LegacyBillingAdoptionDialog
          projectId={projectId}
          workspace={ownerContractQuery.data}
          onClose={() => setAdoptingLegacy(false)}
        />
      )}

      <Dialog
        open={Boolean(overbillFix)}
        onClose={() => !reduceToAuthorizedMut.isPending && setOverbillFix(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: 16, py: 2.5 }}>
          {overbillFix && overbillByPb.has(overbillFix.id)
            ? "This application bills more than the contract allows"
            : "This application won't be accepted yet"}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {overbillFix && (() => {
            const recon = overbillByPb.get(overbillFix.id);
            const over = Number(recon?.overbilling_amount ?? 0);
            const isOverbill = overbillByPb.has(overbillFix.id);
            const blockers = blockersByPb.get(overbillFix.id) ?? [];
            const otherBlockers = blockers.filter(
              (b) => b.code !== "OWNER_SOV_MAX_EXCEEDED",
            );
            const lines = (recon?.lines ?? []).filter(
              (l) => Number(l.overbilling_amount) > 0,
            );
            return (
              <div className="space-y-5">
                {isOverbill ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    Nothing is wrong with the application — it just claims{" "}
                    <strong>{moneyLabel(over)}</strong> more than the owner contract
                    currently authorizes, and the ledger will not accept that. There are
                    two honest ways forward, and only you can say which is right.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    Submitting this application would be refused as it stands. Here is
                    what needs sorting first — none of it is lost work.
                  </Typography>
                )}

                {otherBlockers.length > 0 && (
                  <ul className="list-disc pl-5 space-y-2">
                    {otherBlockers.map((b) => (
                      <li key={b.code}>
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {b.message}
                          {b.code === "OWNER_SOV_REFERENCE_INVALID" && (
                            <>
                              {" "}
                              Use <strong>Adopt legacy billings</strong> above to point
                              those lines at the contract&apos;s schedule.
                            </>
                          )}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                )}

                {isOverbill && (
                <Table size="small" sx={{ "& td": { py: 1.25, borderColor: "divider" } }}>
                  <TableBody>
                    {lines.map((l) => (
                      <TableRow key={l.progress_billing_line_id}>
                        <TableCell sx={{ fontSize: 12, pl: 0 }}>
                          Line {l.line_no}
                          {l.description ? ` — ${l.description}` : ""}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          authorized {moneyLabel(l.maximum_billable)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, pr: 0 }}>
                          billed {moneyLabel(l.completed_and_stored)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )}

                {isOverbill && (
                <div className="rounded border border-slate-200 dark:border-gray-700 p-4 space-y-3">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    If the contract amount is right
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Bill the authorized amount instead. This reopens the application as
                    a draft and lowers this period by {moneyLabel(over)}. Nothing posts
                    until you review and submit it.
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    label="Reason (kept on the record)"
                    value={reduceReason}
                    onChange={(e) => setReduceReason(e.target.value)}
                    disabled={reduceToAuthorizedMut.isPending}
                    // The floating label sits above the border, so without room
                    // of its own it lands on the paragraph above.
                    sx={{ mt: 2 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={!reduceReason.trim() || reduceToAuthorizedMut.isPending}
                    onClick={() =>
                      reduceToAuthorizedMut.mutate({
                        pbId: overbillFix.id,
                        reason: reduceReason.trim(),
                      })
                    }
                  >
                    {reduceToAuthorizedMut.isPending
                      ? "Reducing…"
                      : `Bill ${moneyLabel(Number(recon?.completed_and_stored ?? 0) - over)} instead`}
                  </Button>
                </div>
                )}

                {isOverbill && (
                <div className="rounded border border-slate-200 dark:border-gray-700 p-4 space-y-3">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    If the work is right
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    The schedule needs to come up by {moneyLabel(over)}. A contract value
                    is locked once the contract is active, so that takes an owner-approved
                    change order — raise one for this job, approve it, then post this
                    application unchanged.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setOverbillFix(null);
                      toast.success(
                        `Raise an owner CO on this job for ${moneyLabel(over)} against the `
                        + "SOV line above, then approve it.",
                      );
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      navigate({ to: "/change-orders" as any });
                    }}
                  >
                    Go to change orders
                  </Button>
                </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOverbillFix(null)}
            disabled={reduceToAuthorizedMut.isPending}
          >
            Not now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(invoiceBlock)}
        onClose={() => setInvoiceBlock(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: 16 }}>Can&apos;t post to ledger</DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            {invoiceBlock?.message}
          </Alert>
          {(invoiceBlock?.allowed != null || invoiceBlock?.attempted != null) && (
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    Authorized (SOV)
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {moneyLabel(invoiceBlock?.allowed)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    Completed + stored
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {moneyLabel(invoiceBlock?.attempted)}
                  </TableCell>
                </TableRow>
                {invoiceBlock?.excess != null && (
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                      Over by
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: "error.main" }}>
                      {moneyLabel(invoiceBlock.excess)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <Typography variant="body2" color="text.secondary">
            Open the application and either raise that line&apos;s scheduled/authorized
            value to match what you&apos;ve billed, or lower this period / stored materials
            so completed stays within the SOV. Then post again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceBlock(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const pbId = invoiceBlock?.pbId;
              setInvoiceBlock(null);
              if (pbId) {
                const row = pbs.find((p) => p.id === pbId);
                if (row && (row.status === "approved" || row.status === "invoiced" || row.status === "submitted")) {
                  setAdjusting(row);
                } else if (pbId) {
                  loadDraftForEdit.mutate(pbId);
                }
              }
            }}
          >
            Open to fix
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(adjusting)}
        onClose={() => !openForAdjustmentMut.isPending && setAdjusting(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: 16 }}>
          Adjust application #{adjusting?.application_no}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This reopens the application as a draft. If it (or later apps) were already
            posted to the ledger, those AR invoices are reversed first. When you save,
            later draft apps keep their <strong>percent complete</strong> and refresh
            prior work from this app.
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            required
            multiline
            minRows={2}
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
            placeholder="e.g. Corrected line 1 percent complete after owner review"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAdjusting(null)}
            disabled={openForAdjustmentMut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!adjustReason.trim() || openForAdjustmentMut.isPending}
            onClick={() => {
              if (!adjusting) return;
              openForAdjustmentMut.mutate({
                pbId: adjusting.id,
                reason: adjustReason.trim(),
              });
            }}
          >
            {openForAdjustmentMut.isPending ? "Opening…" : "Open for edit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ── New Progress Billing dialog ───────────────────────────────

const emptySovLine = (retentionPercent = "0") => ({
  sov_item_id: "",
  item_code: "",
  phase_id: "",
  description: "",
  scheduled_value: "0",
  maximum_billable: "0",
  work_completed_previous: "0",
  work_completed_this_period: "0",
  materials_stored_previous: "0",
  materials_stored: "0",
  retention_held_previous: "0",
  retention_percent: retentionPercent,
  work_retainage_percent: retentionPercent,
  material_retainage_percent: retentionPercent,
  remaining_to_bill: "0",
  billable_now: true,
});

type DraftSovLine = ReturnType<typeof emptySovLine>;
type CanonicalBillingGridRow = DraftSovLine & { id: string; rowIndex: number };

function NewProgressBillingDialog({
  projectId,
  onClose,
  existing,
}: {
  projectId: string;
  onClose: () => void;
  /** When set, the dialog edits this DRAFT application instead of creating one. */
  existing?: ProgressBilling | null;
}) {
  const isEdit = Boolean(existing);
  const qc = useQueryClient();
  const toast = useToast();
  const phasesQuery = useQuery({
    queryKey: [...projectKeys.all, "phases", projectId],
    queryFn: () => api<{ id: string; code: string; name: string }[]>(
      `/api/projects/${projectId}/phases`,
    ),
    enabled: Boolean(projectId),
  });
  const phases = phasesQuery.data ?? [];

  // The job's configured retention — same source the SOV importer uses.
  const projectQuery = useQuery({
    queryKey: [...projectKeys.all, "detail", projectId],
    queryFn: () => api<{ retention_percent: string }>(`/api/projects/${projectId}`),
    enabled: Boolean(projectId),
  });
  const jobRetentionPercent = String(projectQuery.data?.retention_percent ?? "0");

  const today = new Date().toISOString().slice(0, 10);
  const [applicationNo, setApplicationNo] = useState(
    existing ? String(existing.application_no) : "1");
  const [periodFrom, setPeriodFrom] = useState(existing?.period_from ?? today);
  const [periodTo, setPeriodTo] = useState(existing?.period_to ?? today);
  const [billingDate, setBillingDate] = useState(existing?.billing_date ?? today);
  const [notes, setNotes] = useState(existing?.notes ?? "");

  const initializationQuery = useQuery({
    queryKey: [...projectKeys.progressBillings(projectId), "initialization", billingDate],
    queryFn: () => api<ProgressBillingInitialization>(
      `/api/projects/${projectId}/progress-billings/initialization?billing_date=${billingDate}`,
    ),
    enabled: !isEdit && Boolean(projectId) && Boolean(billingDate),
    retry: false,
  });

  // G703 schedule-of-values lines — one per phase, all zero by default
  const [lines, setLines] = useState<DraftSovLine[]>([]);
  const canonicalMode = Boolean(existing?.contract_id || initializationQuery.data?.contract_id);

  // Initialise lines: from the draft when editing, otherwise one blank row per
  // phase. Editing must show what was actually saved, not a fresh grid.
  const [linesInitialised, setLinesInitialised] = useState(false);
  useEffect(() => {
    if (!isEdit) setLinesInitialised(false);
  }, [billingDate, isEdit]);
  useEffect(() => {
    if (linesInitialised) return;
    if (existing) {
      setLines(
        (existing.lines ?? []).map((l) => ({
          sov_item_id: l.sov_item_id ?? "",
          item_code: "",
          phase_id: l.phase_id ?? "",
          description: l.description ?? "",
          scheduled_value: String(l.scheduled_value ?? "0"),
          maximum_billable: String(l.scheduled_value ?? "0"),
          work_completed_previous: String(l.work_completed_previous ?? "0"),
          work_completed_this_period: String(l.work_completed_this_period ?? "0"),
          materials_stored_previous: String(l.materials_stored_previous ?? "0"),
          materials_stored: String(l.materials_stored ?? "0"),
          retention_held_previous: String(l.retention_held_previous ?? "0"),
          retention_percent: String(l.retention_percent ?? "0"),
          work_retainage_percent: String(
            l.work_retainage_percent ?? l.retention_percent ?? "0"),
          material_retainage_percent: String(
            l.material_retainage_percent ?? l.retention_percent ?? "0"),
          remaining_to_bill: String(
            Number(l.scheduled_value ?? 0)
            - Number(l.work_completed_previous ?? 0)
            - Number(l.work_completed_this_period ?? 0)
            - Number(l.materials_stored ?? 0)),
          billable_now: true,
        })),
      );
      setLinesInitialised(true);
      return;
    }
    if (initializationQuery.data) {
      setApplicationNo(String(initializationQuery.data.application_no));
      setLines(
        initializationQuery.data.lines.map((l) => ({
          sov_item_id: l.sov_item_id,
          item_code: l.item_code,
          phase_id: "",
          description: l.description,
          scheduled_value: String(l.scheduled_value),
          maximum_billable: String(l.maximum_billable),
          work_completed_previous: String(l.work_completed_previous),
          work_completed_this_period: "0",
          materials_stored_previous: String(l.materials_stored_previous),
          materials_stored: String(l.materials_stored),
          retention_held_previous: String(l.retention_held_previous),
          retention_percent: String(l.work_retainage_percent),
          work_retainage_percent: String(l.work_retainage_percent),
          material_retainage_percent: String(l.material_retainage_percent),
          remaining_to_bill: String(l.remaining_to_bill),
          billable_now: l.billable_now,
        })),
      );
      setLinesInitialised(true);
      return;
    }
    if (
      initializationQuery.isError
      && !phasesQuery.isLoading
      && !projectQuery.isLoading
    ) {
      // Phases are a convenient starting grid when the job has them; the SOV is
      // the billing breakdown agreed with the owner and doesn't require phases,
      // so a job without them starts with one blank line instead of a dead end.
      // Retention defaults to the JOB's configured rate — the SOV importer uses
      // the same source, so the two entry paths can't disagree on one job.
      const projectPhases = phasesQuery.data ?? [];
      setLines(
        projectPhases.length > 0
          ? projectPhases.map((p) => ({
              ...emptySovLine(jobRetentionPercent),
              phase_id: p.id,
              description: `${p.code} ${p.name}`,
            }))
          : [emptySovLine(jobRetentionPercent)],
      );
      setLinesInitialised(true);
    }
  }, [
    existing,
    initializationQuery.data,
    initializationQuery.isError,
    jobRetentionPercent,
    linesInitialised,
    phasesQuery.data,
    phasesQuery.isLoading,
    projectQuery.isLoading,
  ]);

  const updateLine = (idx: number, field: string, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addLine = () =>
    setLines((prev) => [...prev, emptySovLine(jobRetentionPercent)]);
  const removeLine = (idx: number) =>
    setLines((prev) => prev.filter((_, i) => i !== idx));

  const mut = useMutation({
    mutationFn: () =>
      api<ProgressBilling>(
        isEdit
          // cascade=true keeps later apps' % complete after this save.
          ? `/api/projects/progress-billings/${existing!.id}?cascade=true`
          : "/api/projects/progress-billings",
        {
        method: isEdit ? "PUT" : "POST",
        body: {
          // The update schema has no project_id — the application already
          // belongs to a job and can't be moved between them.
          ...(isEdit ? {} : { project_id: projectId }),
          // "" would parse to NaN and serialise as null, which the update
          // path reads as "not sent" — the save would report success and
          // change nothing. Keep the current number instead.
          application_no: Number.isFinite(parseInt(applicationNo, 10))
            ? parseInt(applicationNo, 10)
            : undefined,
          period_from: periodFrom,
          period_to: periodTo,
          billing_date: billingDate,
          // Empty string, not null: null means "leave unchanged" on update, so
          // a note the user deleted would silently come back.
          notes: isEdit ? notes : (notes || null),
          lines: lines.map((l, i) => ({
            line_no: i + 1,
            sov_item_id: l.sov_item_id || null,
            phase_id: l.phase_id || null,
            description: l.description || null,
            scheduled_value: l.scheduled_value,
            ...(canonicalMode
              ? {}
              : { work_completed_previous: l.work_completed_previous }),
            work_completed_this_period: l.work_completed_this_period,
            materials_stored: l.materials_stored,
            retention_percent: l.retention_percent,
            work_retainage_percent: l.work_retainage_percent,
            material_retainage_percent: l.material_retainage_percent,
          })),
        },
      },
      ),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      if (!isEdit) {
        toast.success("Progress billing created");
        onClose();
        return;
      }
      const outcome = describeCascade(saved.cascade);
      if (outcome.warn) toast.error(outcome.message);
      else toast.success(outcome.message);
      onClose();
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const canonicalRows: CanonicalBillingGridRow[] = lines.map((line, rowIndex) => ({
    ...line,
    id: line.sov_item_id || `line-${rowIndex}`,
    rowIndex,
  }));
  const canonicalColumns: GridColDef<CanonicalBillingGridRow>[] = [
    { field: "item_code", headerName: "SOV", width: 90 },
    {
      field: "description",
      headerName: "Description",
      minWidth: 220,
      flex: 1,
      renderCell: ({ row }) => (
        <span>
          {row.description}
          {!row.billable_now && (
            <span className="ml-2 text-[10px] font-semibold uppercase text-slate-400">
              carried history
            </span>
          )}
        </span>
      ),
    },
    {
      field: "scheduled_value",
      headerName: "Revised value",
      width: 130,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.scheduled_value),
    },
    {
      field: "work_completed_previous",
      headerName: "Prior work",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.work_completed_previous),
    },
    {
      field: "work_completed_this_period",
      headerName: "This period",
      width: 135,
      sortable: false,
      renderCell: ({ row }) => (
        <MoneyField
          size="small"
          disabled={!row.billable_now}
          value={row.work_completed_this_period}
          onChange={(value) => updateLine(row.rowIndex, "work_completed_this_period", value)}
          sx={{ width: 118 }}
          inputProps={{ style: { fontSize: 11, textAlign: "right" } }}
        />
      ),
    },
    {
      field: "materials_stored_previous",
      headerName: "Prior stored",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.materials_stored_previous),
    },
    {
      field: "materials_stored",
      headerName: "Stored balance",
      width: 145,
      sortable: false,
      renderCell: ({ row }) => (
        <MoneyField
          size="small"
          disabled={!row.billable_now}
          value={row.materials_stored}
          onChange={(value) => updateLine(row.rowIndex, "materials_stored", value)}
          sx={{ width: 125 }}
          inputProps={{ style: { fontSize: 11, textAlign: "right" } }}
        />
      ),
    },
    {
      field: "work_retainage_percent",
      headerName: "Work ret.",
      width: 95,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => `${Number(row.work_retainage_percent).toFixed(2)}%`,
    },
    {
      field: "material_retainage_percent",
      headerName: "Mat. ret.",
      width: 95,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => `${Number(row.material_retainage_percent).toFixed(2)}%`,
    },
    {
      field: "remaining_to_bill",
      headerName: "Remaining",
      width: 125,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const remaining = Number(row.maximum_billable)
          - Number(row.work_completed_previous)
          - Number(row.work_completed_this_period)
          - Number(row.materials_stored);
        return (
          <span className={remaining < 0 ? "font-semibold text-red-600" : ""}>
            {fmt(remaining)}
          </span>
        );
      },
    },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit
          ? `Edit Application #${existing!.application_no}`
          : "New Progress Billing Application"}
      </DialogTitle>
      <DialogContent dividers>
        <div className="space-y-4 pt-2">
          {/* Header fields */}
          <div className="grid grid-cols-4 gap-3">
            <TextField
              label="Application #"
              size="small"
              type="number"
              value={applicationNo}
              onChange={(e) => setApplicationNo(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Period from"
              size="small"
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Period to"
              size="small"
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Billing date"
              size="small"
              type="date"
              value={billingDate}
              onChange={(e) => setBillingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* G703 schedule of values */}
          <div>
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
              G703 Schedule of Values
            </div>
            {!isEdit && initializationQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : canonicalMode ? (
              <div className="space-y-2">
                <Alert severity="info" sx={{ py: 0.25 }}>
                  Initialized from the canonical owner SOV. Revised values and prior
                  work, stored materials, and retention are system-derived; enter only
                  this period&apos;s work and the current stored-material balance.
                </Alert>
                <Box sx={{ height: 520, width: "100%" }}>
                  <DataGridPro<CanonicalBillingGridRow>
                    {...gridDefaults}
                    rows={canonicalRows}
                    columns={canonicalColumns}
                    density="compact"
                    disableRowSelectionOnClick
                    rowHeight={48}
                    columnHeaderHeight={42}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 100, page: 0 } },
                    }}
                    pageSizeOptions={[50, 100, 250]}
                    sx={{
                      borderColor: "divider",
                      "& .MuiDataGrid-cell": { fontSize: 11 },
                      "& .MuiDataGrid-columnHeaderTitle": { fontSize: 11, fontWeight: 700 },
                    }}
                  />
                </Box>
              </div>
            ) : (
              <>
                {!isEdit && initializationQuery.isError && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Canonical SOV initialization is unavailable: {errorMessage(initializationQuery.error)}.
                    The legacy schedule editor remains available for compatible projects.
                  </Alert>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Sched. value</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Prev. completed</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>This period</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Materials stored</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Ret. %</TableCell>
                      <TableCell sx={{ width: 32 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <TextField
                            select size="small" value={line.phase_id}
                            onChange={(e) => updateLine(idx, "phase_id", e.target.value)}
                            sx={{ minWidth: 110 }} SelectProps={{ displayEmpty: true }}
                            inputProps={{ style: { fontSize: 11 } }}
                          >
                            <MenuItem value=""><span className="text-slate-400">— none —</span></MenuItem>
                            {phases.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                <span style={{ fontFamily: "monospace", fontSize: 11 }}>{p.code}</span>
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={line.description}
                            onChange={(e) => updateLine(idx, "description", e.target.value)}
                            sx={{ minWidth: 160 }} inputProps={{ style: { fontSize: 11 } }} />
                        </TableCell>
                        {(["scheduled_value", "work_completed_previous", "work_completed_this_period", "materials_stored"] as const).map((field) => (
                          <TableCell key={field}>
                            <MoneyField size="small" value={line[field]}
                              onChange={(value) => updateLine(idx, field, value)} sx={{ width: 110 }}
                              inputProps={{ style: { fontSize: 11 } }} />
                          </TableCell>
                        ))}
                        <TableCell>
                          <TextField size="small" type="number" value={line.retention_percent}
                            onChange={(e) => updateLine(idx, "retention_percent", e.target.value)}
                            sx={{ width: 90 }} inputProps={{ style: { fontSize: 11 }, min: 0 }} />
                        </TableCell>
                        <TableCell align="right">
                          <button type="button" title="Remove line" onClick={() => removeLine(idx)}
                            className="text-xs text-slate-400 hover:text-red-600 px-1">✕</button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={addLine}>
                  + Add line
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={mut.isPending || lines.length === 0}
          onClick={() => mut.mutate()}
        >
          {mut.isPending
            ? (isEdit ? "Saving…" : "Creating…")
            : (isEdit ? "Save changes" : "Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ── Legacy adoption dialog ───────────────────────────────────

interface LegacyAdoptionGridRow {
  id: string;
  application_no: number;
  line_no: number;
  description: string;
  scheduled_value: string;
  target_sov_item_id: string;
}

interface LegacyAdoptionResult {
  adopted_lines: number;
  adopted_applications: number;
  stored_material_movements: number;
}

function normaliseDescription(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function LegacyBillingAdoptionDialog({
  projectId,
  workspace,
  onClose,
}: {
  projectId: string;
  workspace: OwnerContractWorkspace;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const legacyLinesQuery = useQuery({
    queryKey: [
      "legacy-progress-billing-lines",
      projectId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () => api<LegacyProgressBillingLinePage>(
      `/api/projects/${projectId}/progress-billings/legacy-lines`
      + `?limit=${paginationModel.pageSize}`
      + `&offset=${paginationModel.page * paginationModel.pageSize}`,
    ),
  });
  const uniqueSovByDescription = new Map<string, string>();
  const ambiguousDescriptions = new Set<string>();
  for (const item of workspace.sov_items) {
    const description = normaliseDescription(item.description);
    if (!description) continue;
    if (uniqueSovByDescription.has(description)) {
      uniqueSovByDescription.delete(description);
      ambiguousDescriptions.add(description);
    } else if (!ambiguousDescriptions.has(description)) {
      uniqueSovByDescription.set(description, item.id);
    }
  }
  const legacyLines = legacyLinesQuery.data?.items ?? [];
  const rows: LegacyAdoptionGridRow[] = legacyLines.map((line) => {
    const description = normaliseDescription(line.description);
    return {
      id: line.id,
      application_no: line.application_no,
      line_no: line.line_no,
      description: line.description ?? `Line ${line.line_no}`,
      scheduled_value: line.scheduled_value,
      target_sov_item_id: mappings[line.id]
        ?? uniqueSovByDescription.get(description)
        ?? "",
    };
  });
  const valueOptions = [
    { value: "", label: "— not mapped —" },
    ...workspace.sov_items.map((item) => ({
      value: item.id,
      label: `${item.item_code} — ${item.description}`,
    })),
  ];
  const columns: GridColDef<LegacyAdoptionGridRow>[] = [
    { field: "application_no", headerName: "App #", width: 80 },
    { field: "line_no", headerName: "Line", width: 70 },
    { field: "description", headerName: "Legacy description", minWidth: 230, flex: 1 },
    {
      field: "scheduled_value",
      headerName: "Scheduled",
      width: 125,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.scheduled_value),
    },
    {
      field: "target_sov_item_id",
      headerName: "Canonical owner SOV target",
      type: "singleSelect",
      valueOptions,
      editable: true,
      minWidth: 300,
      flex: 1,
    },
  ];
  const currentPageMappings = Object.fromEntries(
    rows.map((row) => [row.id, row.target_sov_item_id]),
  );
  const allMappings = { ...currentPageMappings, ...mappings };
  const mappedEntries = Object.entries(allMappings).filter(([, target]) => target);

  const adoptMutation = useMutation({
    mutationFn: () => api<LegacyAdoptionResult>(
      `/api/projects/${projectId}/progress-billings/adopt-canonical-sov`,
      {
        method: "POST",
        body: {
          contract_id: workspace.contract_id,
          mappings: mappedEntries.map(([lineId, sovItemId]) => ({
            progress_billing_line_id: lineId,
            sov_item_id: sovItemId,
          })),
        },
      },
    ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: projectKeys.progressBillings(projectId) });
      qc.invalidateQueries({ queryKey: ["legacy-progress-billing-lines", projectId] });
      toast.success(
        `Adopted ${result.adopted_lines} line${result.adopted_lines === 1 ? "" : "s"} `
        + `across ${result.adopted_applications} application${result.adopted_applications === 1 ? "" : "s"}`,
      );
      onClose();
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Adopt legacy progress billings</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-1">
          <Alert severity="info">
            Map historical billing lines to {workspace.contract_no} — {workspace.title}.
            Unique exact-description matches are preselected; ambiguous descriptions
            stay unmapped. Double-click a target cell to correct it. Adoption preserves
            the applications and derives their stored-
            material and retention history; unmapped lines are left unchanged.
          </Alert>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {mappedEntries.length} of {legacyLinesQuery.data?.total ?? 0} legacy lines mapped
          </div>
          <Box sx={{ height: 560, width: "100%" }}>
            <DataGridPro<LegacyAdoptionGridRow>
              {...gridDefaults}
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              processRowUpdate={(updatedRow) => {
                setMappings((previous) => ({
                  ...previous,
                  [updatedRow.id]: updatedRow.target_sov_item_id,
                }));
                return updatedRow;
              }}
              loading={legacyLinesQuery.isLoading}
              paginationMode="server"
              rowCount={legacyLinesQuery.data?.total ?? 0}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setMappings((previous) => ({ ...currentPageMappings, ...previous }));
                setPaginationModel(model);
              }}
              pageSizeOptions={[50, 100, 250]}
              sx={{
                borderColor: "divider",
                "& .MuiDataGrid-cell": { fontSize: 11 },
                "& .MuiDataGrid-columnHeaderTitle": { fontSize: 11, fontWeight: 700 },
              }}
            />
          </Box>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={adoptMutation.isPending || mappedEntries.length === 0}
          onClick={() => adoptMutation.mutate()}
        >
          {adoptMutation.isPending
            ? "Adopting…"
            : `Adopt ${mappedEntries.length} mapped line${mappedEntries.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ── Upload SOV dialog ─────────────────────────────────────────
// The schedule of values is the billing breakdown agreed with the owner — not
// the cost budget — so it arrives as its own spreadsheet. Drop it here and it
// becomes a DRAFT application (same importer framework as every other upload:
// AI column mapping, no-write preview, then commit).

interface SovPreviewSummary {
  application_no?: number;
  lines?: number;
  total_scheduled_value?: string;
  errors?: number;
}

interface SovUploadResponse {
  preview?: { will_create_count: number; error_count: number; summary: SovPreviewSummary;
              rows: { row_no: number; status: string; message: string | null }[] };
  result?: { created_count: number; error_count: number; summary: SovPreviewSummary };
}

function UploadSovDialog({
  projectCode,
  onClose,
  onDone,
  hasOwnerContract,
  onSetUpContract,
}: {
  projectCode: string;
  onClose: () => void;
  onDone: () => void;
  hasOwnerContract: boolean;
  onSetUpContract: () => void;
}) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<SovUploadResponse["preview"] | null>(null);
  // Which worksheet to read. Empty means "let the server pick the sheet whose
  // header matches" — a pay-app pack usually hides the G703 behind a cover tab.
  const [sheet, setSheet] = useState("");

  const send = async (
    endpoint: "preview" | "commit",
    f: File,
    forSheet: string,
  ): Promise<SovUploadResponse> => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", "sov");
    fd.append("context_json", JSON.stringify({ project: projectCode }));
    // Commit must read the same sheet the preview showed, or the user approves
    // one tab's numbers and commits another's.
    if (forSheet) fd.append("sheet", forSheet);
    const token = getToken();
    return $fetch(`/api/imports/upload/${endpoint}`, {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  };

  const runPreview = async (f: File, forSheet = "") => {
    setFile(f);
    setSheet(forSheet);
    setBusy(true);
    setPreview(null);
    try {
      const res = await send("preview", f, forSheet);
      setPreview(res.preview ?? null);
    } catch (err) {
      toast.error(errorMessage(err));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await send("commit", file!, sheet);
      const n = res.result?.created_count ?? 0;
      toast.success(
        `Application #${res.result?.summary?.application_no} drafted — ${n} line${n === 1 ? "" : "s"}`,
      );
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const errRows = (preview?.rows ?? []).filter((r) => r.status === "error");
  const sheetSummary = preview?.summary as
    | { sheet_name?: string; sheet_names?: string[]; sheet_note?: string }
    | undefined;
  const sheetNames = sheetSummary?.sheet_names ?? [];
  const readSheet = sheetSummary?.sheet_name ?? "";
  const sheetNote = sheetSummary?.sheet_note;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Schedule of Values — {projectCode}</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-1">
          {!hasOwnerContract && (
            <Alert
              severity="info"
              sx={{ fontSize: 12 }}
              action={(
                <Button size="small" onClick={onSetUpContract}>
                  Set up the contract
                </Button>
              )}
            >
              Does this job bill against an <strong>owner contract</strong>? Put the
              schedule on the contract instead. Every application then inherits it, the
              previous columns are computed for you, and there is only ever one schedule
              to keep straight. Uploading here builds a schedule that lives on this one
              application.
            </Alert>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Needs a <b>description</b> and a <b>scheduled value</b> column (G703
            headers like “Description of Work” work as-is — title rows above the
            header are fine, and a cover sheet in front of the G703 is too).
            Creates a <b>standalone draft pay application</b> for this job. If the job
            bills against an owner contract SOV, use <b>+ New Progress Billing</b> instead —
            this upload builds a separate schedule that the contract will not accept.
          </p>
          <FileDropZone
            file={file}
            onFile={(f) => runPreview(f)}
            onClear={() => { setFile(null); setPreview(null); setSheet(""); }}
            accept=".xlsx,.xlsm,.csv,.tsv"
            label="Drop the SOV spreadsheet here"
            sublabel="Excel or CSV"
            disabled={busy}
          />
          {sheetNames.length > 1 && (
            <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
              <label htmlFor="sov-sheet" className="block font-medium">
                Worksheet
              </label>
              <select
                id="sov-sheet"
                className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900"
                value={sheet || readSheet}
                disabled={busy || !file}
                onChange={(e) => { if (file) runPreview(file, e.target.value); }}
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {sheetNote && <p className="text-amber-800 dark:text-amber-300">{sheetNote}</p>}
            </div>
          )}
          {busy && <CircularProgress size={20} />}
          {preview && (
            <div className="text-sm text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-700 rounded px-3 py-2">
              <div>
                <b>{preview.will_create_count}</b> line
                {preview.will_create_count === 1 ? "" : "s"} · application #
                {preview.summary?.application_no} · total $
                {Number(preview.summary?.total_scheduled_value ?? 0).toLocaleString()}
              </div>
              {preview.will_create_count === 0 && (
                <div className="mt-2 text-xs text-amber-800 dark:text-amber-300 space-y-1">
                  <p>
                    {(preview.summary as { parse_hint?: string } | undefined)?.parse_hint
                      ?? "No SOV lines were recognized. Need a Description of Work column and a Scheduled Value column."}
                  </p>
                  {Array.isArray((preview.summary as { detected_headers?: string[] } | undefined)?.detected_headers)
                    && (preview.summary as { detected_headers: string[] }).detected_headers.length > 0 && (
                    <p>
                      Detected headers:{" "}
                      {(preview.summary as { detected_headers: string[] }).detected_headers.join(", ")}
                    </p>
                  )}
                </div>
              )}
              {errRows.length > 0 && (
                <ul className="mt-1 text-xs text-red-600 dark:text-red-400 list-disc pl-4">
                  {errRows.slice(0, 5).map((r) => (
                    <li key={r.row_no}>row {r.row_no}: {r.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !preview || preview.will_create_count === 0}
          onClick={commit}
        >
          {busy ? "Working…" : "Create draft application"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
